/**
 * 管理者の同席（2026-10-14 柴田決定・方式B「同席コード」）。管理者の側。
 *
 * 作業者がマニュアルを開くときに入力する6桁のコードを出す。コードは60秒で切れ、
 * 1回使うと無効になる。表示している間は、期限切れ・使用済みになったら次のコードを
 * 自動で出す（作業者が続けて入力できるように）。
 *
 * 下の表は、自分が同席している閲覧の一覧。作業者が画面を閉じると消える。
 * 立ち去るときなどは、ここから打ち切れる。
 */
import { useCallback, useEffect, useState } from "react";
import { onSnapshot, query, where } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { app } from "../firebase";
import { useAuth } from "../auth";
import { localeOf, useI18n } from "../lib/i18n";
import { PRESENCE_TTL_MS, type PresenceDoc } from "../lib/types";

const functions = getFunctions(app, "asia-northeast1");
const issuePresenceCode = httpsCallable<void, { code: string; expiresAtMs: number }>(functions, "issuePresenceCode");
const endPresence = httpsCallable<{ dishId: string; uid: string }, { ok: boolean }>(functions, "endPresence");

type Used = { name: string | null };

export function Presence() {
  const { user, profile, paths } = useAuth();
  const { t, lang } = useI18n();
  const isAdmin = profile?.role === "admin";

  const [showing, setShowing] = useState(false);
  const [current, setCurrent] = useState<{ code: string; expiresAtMs: number } | null>(null);
  const [lastUsed, setLastUsed] = useState<Used | null>(null);
  const [now, setNow] = useState(Date.now());
  const [error, setError] = useState<string | null>(null);
  const [sessions, setSessions] = useState<(PresenceDoc & { id: string })[]>([]);
  const [ending, setEnding] = useState<string | null>(null);

  const issue = useCallback(async () => {
    setError(null);
    try {
      const r = await issuePresenceCode();
      setCurrent(r.data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setShowing(false);
      setCurrent(null);
    }
  }, []);

  // 残り秒数の表示と、一覧の「同席中か」の判定のための時計
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, []);

  // 表示を始めたら最初のコードを出す
  useEffect(() => {
    if (showing && !current) void issue();
  }, [showing, current, issue]);

  // 期限が来たら次のコードを出す
  useEffect(() => {
    if (showing && current && now >= current.expiresAtMs) {
      setCurrent(null);
    }
  }, [showing, current, now]);

  // 使われたら、誰が開いたかを出して次のコードを出す
  useEffect(() => {
    if (!showing || !current || !paths) return;
    return onSnapshot(paths.presenceCodeDoc(current.code), (snap) => {
      const used = snap.get("usedBy") as { name?: string } | null | undefined;
      if (used) {
        setLastUsed({ name: used.name ?? null });
        setCurrent(null);
      }
    });
  }, [showing, current, paths]);

  // 自分が同席している閲覧
  useEffect(() => {
    if (!isAdmin || !paths || !user) return;
    return onSnapshot(
      query(paths.presence(), where("adminUid", "==", user.uid)),
      (snap) => setSessions(snap.docs.map((d) => ({ id: d.id, ...(d.data() as PresenceDoc) }))),
      (e) => setError(e.message),
    );
  }, [isAdmin, paths, user]);

  if (!isAdmin) {
    return (
      <main className="page">
        <p className="muted">{t("この画面は管理者のみ利用できます。")}</p>
      </main>
    );
  }

  const active = sessions.filter((s) => s.lastSeenAt && now - s.lastSeenAt.toMillis() <= PRESENCE_TTL_MS);
  const remain = current ? Math.max(0, Math.ceil((current.expiresAtMs - now) / 1000)) : null;

  return (
    <main className="page presence-page">
      <div className="page-head">
        <h1>{t("同席コード")}</h1>
      </div>

      <section className="card">
        <p className="muted small">
          {t("作業者がマニュアルを開くときは、管理者の同席が必要です。作業者のそばでこのコードを見せ、作業者の画面に入力してもらってください。コードは60秒で切り替わり、1回使うと無効になります。")}
        </p>
        {showing ? (
          <>
            <p className="presence-code" aria-live="polite">
              {current ? `${current.code.slice(0, 3)} ${current.code.slice(3)}` : "--- ---"}
            </p>
            {remain !== null && <p className="muted small center-text">{t("あと {n} 秒", { n: remain })}</p>}
            {lastUsed && (
              <p className="ok small center-text">
                {t("{name} さんが閲覧を始めました。", { name: lastUsed.name ?? "" })}
              </p>
            )}
            <button className="link" onClick={() => { setShowing(false); setCurrent(null); }}>
              {t("表示をやめる")}
            </button>
          </>
        ) : (
          <button onClick={() => { setLastUsed(null); setShowing(true); }}>{t("同席コードを表示する")}</button>
        )}
        {error && <p className="error">{error}</p>}
      </section>

      <section className="card">
        <h2>{t("同席中の閲覧")}</h2>
        {active.length === 0 ? (
          <p className="muted">{t("いま同席している閲覧はありません。")}</p>
        ) : (
          <div className="tablewrap">
            <table className="table">
              <thead>
                <tr>
                  <th>{t("作業者")}</th>
                  <th>{t("品目")}</th>
                  <th>{t("開始")}</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {active.map((s) => (
                  <tr key={s.id}>
                    <td>{s.workerName ?? s.uid}</td>
                    <td>{s.dishName ?? s.dishId}</td>
                    <td className="muted small">
                      {s.startedAt?.toDate().toLocaleTimeString(localeOf(lang), { hour: "2-digit", minute: "2-digit" }) ?? ""}
                    </td>
                    <td>
                      <button
                        className="link danger-link"
                        disabled={ending !== null}
                        onClick={async () => {
                          setEnding(s.id);
                          try {
                            await endPresence({ dishId: s.dishId, uid: s.uid });
                          } catch (e) {
                            setError(e instanceof Error ? e.message : String(e));
                          } finally {
                            setEnding(null);
                          }
                        }}
                      >
                        {t("打ち切る")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="muted small">{t("打ち切ると、作業者はすぐにマニュアルを新しく読み込めなくなり、画面も30秒ほどで閉じます。続けるにはもう一度コードの入力が必要です。")}</p>
      </section>
    </main>
  );
}
